import { useEffect, useState } from "react";

/**
 * Menu cards that are not quite still: every few seconds ONE card does a small
 * hop, or now and then turns over and back, so a menu left open reads as a
 * table with cards on it rather than a picture of one.
 *
 * One at a time, never the same card twice running, and never on a schedule:
 * a fixed beat, or two cards moving at once, is a pattern the eye locks onto
 * and the point is the opposite - something the player half-notices.
 *
 * The motion itself is CSS (`.card-fidget-hop` / `.card-fidget-flip` in
 * styles.css), on a wrapper INSIDE each card, so it never fights the card's own
 * placement transform in the fan or on the felt. Off under reduced motion.
 */

export type FidgetKind = "hop" | "flip";

export interface Fidget {
  index: number;
  kind: FidgetKind;
}

/** The quiet stretch between two fidgets, in ms: long enough to be a surprise. */
export const FIDGET_GAP_MIN_MS = 2600;
export const FIDGET_GAP_MAX_MS = 5200;
/** How often a fidget is a turn-over rather than a hop. */
export const FIDGET_FLIP_CHANCE = 0.25;

/**
 * The next fidget: any card but the one that moved last, a hop mostly and a
 * flip sometimes, and the wait before it. Pure, given `random`.
 */
export function nextFidget(count: number, last: number | null, random: () => number = Math.random): { fidget: Fidget; waitMs: number } {
  const pool = Array.from({ length: count }, (_, i) => i).filter((i) => i !== last || count === 1);
  const index = pool[Math.floor(random() * pool.length)] ?? 0;
  const kind: FidgetKind = random() < FIDGET_FLIP_CHANCE ? "flip" : "hop";
  const waitMs = FIDGET_GAP_MIN_MS + random() * (FIDGET_GAP_MAX_MS - FIDGET_GAP_MIN_MS);
  return { fidget: { index, kind }, waitMs };
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The card currently fidgeting among `count`, or null. The caller puts
 * `fidgetClass(...)` on that card's wrapper and calls `done` from its
 * `onAnimationEnd`, which clears it; the next one is already scheduled.
 */
export function useCardFidget(count: number): { fidget: Fidget | null; done: () => void } {
  const [fidget, setFidget] = useState<Fidget | null>(null);
  useEffect(() => {
    if (count <= 0 || reducedMotion()) return;
    let last: number | null = null;
    let timer = 0;
    const schedule = () => {
      const { fidget: next, waitMs } = nextFidget(count, last);
      timer = window.setTimeout(() => {
        // A hidden tab has nobody to surprise, and a pile of queued timers
        // firing on return would set every card off at once.
        if (!document.hidden) {
          last = next.index;
          setFidget(next);
        }
        schedule();
      }, waitMs);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [count]);
  return { fidget, done: () => setFidget(null) };
}

/** The class for card `index`'s fidget wrapper. */
export function fidgetClass(fidget: Fidget | null, index: number): string {
  return fidget && fidget.index === index ? `card-fidget card-fidget-${fidget.kind}` : "card-fidget";
}
